import { createGtm } from '@gtm-support/vue-gtm';
import { isBrowser } from '@nzyme/dom-utils/isBrowser.js';
import { EnvVariables } from '@nzyme/ioc/helpers/EnvVariables.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { consoleLog, LoggerTransport } from '@nzyme/logging/LoggerTransport.js';
import { PrettyBrowserLoggerTransport } from '@nzyme/logging/PrettyBrowserLoggerTransport.js';
import { createContainer } from '@nzyme/vue-ioc/createContainer.js';
import { IocPlugin } from '@nzyme/vue-ioc/IocPlugin.js';
// From core, not `@sentry/browser`: this plugin also runs during SSR, and `addBreadcrumb` is core's
// function anyway — importing it here keeps the browser SDK out of the server module graph.
import { addBreadcrumb } from '@sentry/core';
import chalk from 'chalk';
import { useHead, useNuxtApp, useRouter, useRuntimeConfig } from 'nuxt/app';
import { nextTick } from 'vue';
import { BrowserCountryContext } from '@sigma/client/i18n/BrowserCountryContext.js';
import { BrowserLanguageContext } from '@sigma/client/i18n/BrowserLanguageContext.js';
import { TrackingService } from '@sigma/client/services/TrackingService.js';
import { AppConfig } from '@sigma/common/AppConfig.js';
import { createTelemetryTransport } from '@sigma/common/telemetry/createTelemetryTransport.js';
import { createEnvRuntimeConfig } from '@sigma/env/createEnvRuntimeConfig.js';
import { E2E, LOCAL, PRODUCTION } from '@sigma/env/environments.js';
import { RuntimeConfig } from '@sigma/env/RuntimeConfig.js';
import { installTestHarness } from './installTestHarness.js';
import { toLogBreadcrumb } from './toLogBreadcrumb.js';
export function setupApp(appConfig) {
    const container = createContainer();
    const nuxtApp = useNuxtApp();
    const router = useRouter();
    const runtimeConfig = useRuntimeConfig().public;
    nuxtApp.vueApp.use(IocPlugin, { container });
    container.set(AppConfig, appConfig);
    container.set(EnvVariables, runtimeConfig.env);
    // A browser has no config document. The keys an app deliberately ships to the client — never a
    // real secret — travel in the same `env` block Nuxt bakes into `runtimeConfig.public`.
    container.set(RuntimeConfig, createEnvRuntimeConfig(runtimeConfig.env));
    // Setup i18n
    container.register(BrowserLanguageContext);
    container.register(BrowserCountryContext);
    const langContext = container.resolve(BrowserLanguageContext);
    // The factory owns the Sentry hand-off as well as the PHI filter, so the line the browser writes
    // and the error it reports cannot drift apart — see `createTelemetryTransport`.
    const loggerTransport = createTelemetryTransport(getLogWriter());
    container.set(LoggerTransport, loggerTransport);
    if (isBrowser() && !PRODUCTION) {
        // Add logging to the router
        const logger = Logger.create({ name: 'vue-router', transport: loggerTransport });
        router.beforeEach((to, from) => {
            logger.info(`before ${chalk.green(from.fullPath)} → ${chalk.green(to.fullPath)}`, { to, from });
        });
        router.afterEach((to, from, failure) => {
            logger.info(`after ${chalk.green(from.fullPath)} → ${chalk.green(to.fullPath)}`, { to, from, failure });
        });
    }
    if (import.meta.env.NODE_ENV === 'development' && isBrowser()) {
        requestIdleCallback(() => void installSourceMaps());
    }
    useHead({
        htmlAttrs: {
            lang: langContext,
        },
    });
    // Setup GTM
    if (runtimeConfig.gtmId) {
        nuxtApp.vueApp.use(createGtm({
            id: runtimeConfig.gtmId,
            defer: true,
            compatibility: true,
            debug: !PRODUCTION,
        }));
        if (isBrowser()) {
            // Resolved per navigation, not here: `TrackingService` depends on the `AuthStore`
            // interface, which each app registers only after this function returns. Resolving it
            // now would throw `Interface AuthStore was not found` during app init and take down
            // every page. The container caches the instance, so this stays a single service.
            router.afterEach((to, from) => {
                void nextTick(() => container.resolve(TrackingService).trackEvent({
                    event: from.name ? 'virtualPageView' : 'pageView',
                    page: {
                        referrer: window.location.origin + from.fullPath,
                        location: window.location.origin + to.fullPath,
                        page: to.fullPath,
                        title: document.title,
                    },
                }));
            });
        }
    }
    if (isBrowser() && E2E) {
        installTestHarness(container);
    }
    return container;
}
async function installSourceMaps() {
    const { Buffer } = await import('node:buffer');
    globalThis.Buffer = Buffer;
    const { install } = await import('source-map-support');
    install();
}
/**
 * Where a browser log line goes, which is the one thing that differs by environment.
 *
 * On `LOCAL` it goes to a pretty console transport. On every deployed environment it also becomes
 * a Sentry breadcrumb, riding along on the next captured error to give the context that makes it
 * triageable — the payload is already reduced by `createTelemetryTransport` before it reaches
 * here, so the breadcrumb carries scalars only. On deployed non-production environments (testing,
 * demo) it additionally goes to the console, where a devtools line is still useful.
 */
function getLogWriter() {
    if (LOCAL) {
        return PrettyBrowserLoggerTransport.create();
    }
    return (logger, level, message, obj) => {
        // A no-op until `Sentry.init` has run; the Nuxt module injects it at `order: 0`, ahead of
        // the plugin that calls `setupApp`, so in practice the client is always there.
        addBreadcrumb(toLogBreadcrumb(logger, level, message, obj));
        if (!PRODUCTION) {
            consoleLog(logger, level, message, obj);
        }
    };
}
//# sourceMappingURL=setupApp.js.map